import { useCallback, useMemo } from 'react';

import type { DataTableFeatures, DataTableFilterPanelGroup } from 'design-system';

import type { CatalogItem } from '@/entities/catalog-item';
import { countRecentlyLaunched } from '@/entities/catalog-item';
import { transformCamelCaseToSpaces } from '@/shared/lib/format/transformCamelCaseToSpaces';
import type { CountedLabelFunction } from '../../../constants';
import { alphabeticallySortedFilters, labeledFilterColumns } from '../../../constants';
import { countOccurrence } from './lib/countOccurrence';

import type { ReactTable, Row } from '@tanstack/react-table';

export type FilterOption = { label: string; count: number };

const collectLabelCounts = (
  rows: Array<Row<DataTableFeatures, CatalogItem>>,
  key: string,
  joined: boolean | undefined
) => {
  const counts: { [label: string]: number } = {};
  for (const row of rows) {
    if (joined) {
      const label: string | undefined = row.getValue(key);
      const labels: string[] | undefined = label?.split(',');
      for (const l of labels ?? []) {
        countOccurrence(counts, l);
      }
    } else {
      const label: string | undefined = row.getValue(key);
      if (label) countOccurrence(counts, label);
    }
  }
  return counts;
};

const useTableFilters = (table: ReactTable<DataTableFeatures, CatalogItem>) => {
  const filteredRows: Array<Row<DataTableFeatures, CatalogItem>> = table.getFilteredRowModel().rows;
  const allRows: Array<Row<DataTableFeatures, CatalogItem>> = table.getPreFilteredRowModel().rows;

  const getCountedLabels: CountedLabelFunction = useCallback(
    (key: string, joined?: boolean): FilterOption[] => {
      // The set of options and their order come from the whole, unfiltered dataset so
      // an option never appears/disappears or reshuffles as filters (in this group or
      // any other) are toggled — that reflow was what made the list feel like it was
      // scrolling on its own. Only the displayed count reflects the live selection,
      // faceted on every *other* column so this group's own filter doesn't zero out
      // its own sibling options.
      const globalCounts = collectLabelCounts(allRows, key, joined);
      const facetRows = table.getColumn(key)?.getFacetedRowModel().rows ?? filteredRows;
      const facetCounts = collectLabelCounts(facetRows, key, joined);

      return Object.keys(globalCounts)
        .map(label => ({ label, count: facetCounts[label] ?? 0 }))
        .toSorted((a, b) => {
          if (alphabeticallySortedFilters.includes(key)) {
            return a.label.localeCompare(b.label);
          }
          return globalCounts[b.label] - globalCounts[a.label];
        });
    },
    [allRows, filteredRows, table]
  );

  const columnFiltersWithCounts = useMemo(
    () =>
      Object.entries(labeledFilterColumns).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: value(getCountedLabels)
        }),
        {} as Record<string, FilterOption[]>
      ),
    [getCountedLabels]
  );

  const newDataCount = useMemo(
    () =>
      countRecentlyLaunched(
        filteredRows.map(row => ({ LaunchDate: row.getValue<string | undefined>('LaunchDate') })),
        new Date()
      ),
    [filteredRows]
  );

  const groups = useMemo<DataTableFilterPanelGroup<CatalogItem>[]>(
    () =>
      Object.entries(columnFiltersWithCounts).map(([key, options]) => ({
        column: table.getColumn(key)!,
        label: transformCamelCaseToSpaces(key),
        options: options.map(({ label, count }) => ({ label, value: label, count }))
      })),
    [columnFiltersWithCounts, table]
  );

  return {
    columnFiltersWithCounts,
    groups,
    newDataCount
  };
};

export default useTableFilters;
